/**
 * useCblPreview — hook wrapping the new `/api/cbl_check/preview/` endpoint
 * that the backend agent is building in parallel.
 *
 * Graceful degradation: when the preview endpoint returns 404 (not yet
 * deployed), we fall back to the existing `/cbl_check/?search=` endpoint and
 * synthesize a `CblPreviewResponse` using the `is_on_2026_ll97_cbl` flag on
 * `NycCblEntrySerializer`. The fallback path loses the rich
 * `filingContexts[]` preview (no deadline / fee / pathway) but keeps the
 * three-outcome branch decision correct. That's the low-risk degrade
 * described in the spec's open-question #5.
 *
 * The user types a single term; we classify it locally as BBL / BIN /
 * address before hitting the network. Validation errors (10 digits for BBL,
 * 7 digits for BIN) are surfaced in the hook's `inputError` so the step
 * component can disable "Continue" without firing a doomed network call.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import api from "@/api/base";
import type { CblPreviewMatch, CblPreviewResponse, CblIdentity } from "./types";
import type { FilingContext } from "@/api/filingContext";

// ---------------------------------------------------------------------------
// Search-term classification
// ---------------------------------------------------------------------------

export type SearchKind = "bbl" | "bin" | "address";

export interface ClassifiedSearch {
  kind: SearchKind;
  value: string;
  error: string | null;
}

/**
 * Classify a typed search term. Digits-only with specific lengths resolve
 * to BBL/BIN; everything else is treated as an address for the backend's
 * `icontains` match. Partial digit strings (e.g., "12345") surface a
 * validation error so the Continue button disables.
 */
export function classifySearchTerm(raw: string): ClassifiedSearch {
  const term = raw.trim();
  if (term.length < 5) {
    return {
      kind: "address",
      value: term,
      error: "Enter at least 5 characters.",
    };
  }
  const isAllDigits = /^\d+$/.test(term);
  if (isAllDigits) {
    if (term.length === 10) {
      const boroughDigit = term.charAt(0);
      if (!"12345".includes(boroughDigit)) {
        return {
          kind: "bbl",
          value: term,
          error: "BBL must start with a borough digit (1–5).",
        };
      }
      return { kind: "bbl", value: term, error: null };
    }
    if (term.length === 7) {
      const boroughDigit = term.charAt(0);
      if (!"12345".includes(boroughDigit)) {
        return {
          kind: "bin",
          value: term,
          error: "BIN must start with a borough digit (1–5).",
        };
      }
      return { kind: "bin", value: term, error: null };
    }
    return {
      kind: "address",
      value: term,
      error:
        "Digit-only inputs must be exactly 10 digits (BBL) or 7 digits (BIN).",
    };
  }
  return { kind: "address", value: term, error: null };
}

// ---------------------------------------------------------------------------
// Fallback normalizer — synthesizes a CblPreviewResponse from the legacy
// /cbl_check/ endpoint. Loses filingContexts[] but preserves the three
// outcome branches.
// ---------------------------------------------------------------------------

interface LegacyCblEntry {
  bBL?: string;
  bIN?: string;
  address?: string;
  isOn2026Ll97Cbl?: boolean;
  ll97Cbl?: Record<string, unknown> | null;
  ll88Cbl?: Record<string, unknown> | null;
}

function legacyEntryToMatch(entry: LegacyCblEntry): CblPreviewMatch {
  const identity: CblIdentity = {
    address: entry.address ?? null,
    bin: entry.bIN ?? null,
    bbl: entry.bBL ?? null,
    hpdOwnerName: null,
    // The legacy /cbl_check/ endpoint pre-dates the Sprint-7 resolver chain
    // so it can't supply class/year. Null is the documented degraded shape.
    buildingClass: null,
    yearBuilt: null,
  };
  // Authoritative: the backend computes `is_on_2026_ll97_cbl` against the
  // rollover. If absent (stale API), a non-null ll97_cbl counts as "on CBL"
  // for the fallback path — we accept the small chance of false positives
  // on delisted rows in exchange for not blocking the user.
  const onCbl = entry.isOn2026Ll97Cbl ?? Boolean(entry.ll97Cbl) ?? false;
  return {
    identity,
    outcome: onCbl ? "on_2026_cbl" : "delisted",
    filingContexts: [],
    alreadyTrackedBuildingId: null,
  };
}

function emptyNeverOnCblResponse(
  term: string,
  kind: SearchKind,
): CblPreviewResponse {
  const identity: CblIdentity = {
    address: kind === "address" ? term : null,
    bin: kind === "bin" ? term : null,
    bbl: kind === "bbl" ? term : null,
    hpdOwnerName: null,
    buildingClass: null,
    yearBuilt: null,
  };
  return {
    matches: [
      {
        identity,
        outcome: "never_on_cbl",
        filingContexts: [],
        alreadyTrackedBuildingId: null,
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// New single-match response normalizer. The backend's /cbl_check/preview/
// endpoint always returns a single-building result (it picks one row even for
// free-text address searches). Wrap it into the FE's matches[] shape.
// ---------------------------------------------------------------------------

interface SinglePreviewCblEntry {
  bBL?: string | null;
  bIN?: string | null;
  address?: string | null;
}

interface SinglePreviewResponse {
  outcome: CblPreviewMatch["outcome"];
  cblEntry?: SinglePreviewCblEntry | null;
  filingContextPreview?: FilingContext | null;
  disputeGuidance?: unknown;
}

function singleResponseToMatch(resp: SinglePreviewResponse): CblPreviewMatch {
  const entry = resp.cblEntry ?? {};
  const identity: CblIdentity = {
    address: entry.address ?? null,
    bbl: entry.bBL ?? null,
    bin: entry.bIN ?? null,
    hpdOwnerName: null,
    // Single-response shape predates the resolver chain — class/year arrive
    // only via the new matches[] response.
    buildingClass: null,
    yearBuilt: null,
  };
  return {
    identity,
    outcome: resp.outcome,
    filingContexts: resp.filingContextPreview ? [resp.filingContextPreview] : [],
    alreadyTrackedBuildingId: null,
  };
}

// ---------------------------------------------------------------------------
// Network
// ---------------------------------------------------------------------------

async function fetchPreview(
  classified: ClassifiedSearch,
  signal?: AbortSignal,
): Promise<CblPreviewResponse> {
  const params: Record<string, string | number> = { year: 2026 };
  if (classified.kind === "bbl") params.bbl = classified.value;
  else if (classified.kind === "bin") params.bin = classified.value;
  else params.search = classified.value;

  // Try the new preview endpoint first.
  try {
    const response = await api.get<unknown>("/cbl_check/preview/", {
      params,
      signal,
    });
    const body = response.data as
      | { matches?: unknown }
      | SinglePreviewResponse
      | null
      | undefined;
    if (body && Array.isArray((body as { matches?: unknown }).matches)) {
      // Server returns `filingContextPreview` (singular, after camelize) per
      // match. The FE type is `filingContexts: FilingContext[]`. Without
      // this normalization, OnCblPanel destructures `match.filingContexts`
      // and crashes on `.length` (Sprint 7 / Path B regression). Mirror
      // the singleResponseToMatch() shape — drop unknown server-side
      // fields, fill the FE-required ones from preview hints.
      const raw = body as {
        matches: Array<
          {
            outcome: CblPreviewMatch["outcome"];
            identity?: Partial<CblIdentity>;
            filingContextPreview?: FilingContext | null;
            alreadyTrackedBuildingId?: string | null;
          }
        >;
      };
      return {
        matches: raw.matches.map((m) => ({
          identity: {
            address: m.identity?.address ?? null,
            bin: m.identity?.bin ?? null,
            bbl: m.identity?.bbl ?? null,
            hpdOwnerName: m.identity?.hpdOwnerName ?? null,
            buildingClass: m.identity?.buildingClass ?? null,
            yearBuilt: m.identity?.yearBuilt ?? null,
          },
          outcome: m.outcome,
          filingContexts: m.filingContextPreview ? [m.filingContextPreview] : [],
          alreadyTrackedBuildingId: m.alreadyTrackedBuildingId ?? null,
        })),
      };
    }
    if (body && typeof (body as SinglePreviewResponse).outcome === "string") {
      return {
        matches: [singleResponseToMatch(body as SinglePreviewResponse)],
      };
    }
  } catch (err: unknown) {
    // 404 on the preview endpoint → fall through to the legacy path.
    // Any other status we re-raise so the UI can show a real error.
    const isAxios = (e: unknown): e is { response?: { status?: number } } =>
      Boolean(e) && typeof e === "object";
    if (isAxios(err) && err.response?.status && err.response.status !== 404) {
      throw err;
    }
  }

  // Legacy fallback.
  try {
    const legacy = await api.get<LegacyCblEntry[]>("/cbl_check/", {
      params: { search: classified.value },
      signal,
    });
    const entries = Array.isArray(legacy.data) ? legacy.data : [];
    if (entries.length === 0) {
      return emptyNeverOnCblResponse(classified.value, classified.kind);
    }
    return { matches: entries.map(legacyEntryToMatch) };
  } catch (err: unknown) {
    const isAxios = (e: unknown): e is { response?: { status?: number } } =>
      Boolean(e) && typeof e === "object";
    if (isAxios(err) && err.response?.status === 404) {
      return emptyNeverOnCblResponse(classified.value, classified.kind);
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseCblPreviewResult {
  classified: ClassifiedSearch;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  data: CblPreviewResponse | undefined;
}

/**
 * Explicit-submit CBL preview.
 *
 * Two-input contract:
 *   - ``rawTerm`` drives ``classified`` so the UI can render the instant
 *     "BIN must be 7 digits" inline validation while the user types.
 *   - ``submittedTerm`` drives the actual network fetch + queryKey. Until
 *     the caller passes a non-empty submittedTerm (Search button click /
 *     Enter keypress), NO fetch fires.
 *
 * Why no debounce: 2026-05-16 measurement showed pure debounce at any
 * realistic typing pace (>500ms inter-key gaps) collapses to ~10 fetches
 * for a 15-char address — debounce is a band-aid that hides the structural
 * mismatch between "type-as-search" UX and a 4.25s BE response time.
 * Explicit submit gives one fetch per intent, predictable spinner UX,
 * and aligns with the user's mental model ("I'm searching for THIS
 * specific building, not browsing").
 *
 * AbortController via TanStack Query's ``signal`` cancels any prior
 * in-flight fetch on a new submit — guarantees the LATEST submission's
 * result wins even if the network reorders responses.
 */
export function useCblPreview(
  rawTerm: string,
  submittedTerm: string,
): UseCblPreviewResult {
  const classifiedNow = useMemo(
    () => classifySearchTerm(rawTerm),
    [rawTerm],
  );
  const classifiedForFetch = useMemo(
    () => classifySearchTerm(submittedTerm),
    [submittedTerm],
  );
  const enabled =
    !classifiedForFetch.error && classifiedForFetch.value.length >= 5;
  const query = useQuery<CblPreviewResponse>({
    queryKey: [
      "cblPreview",
      classifiedForFetch.kind,
      classifiedForFetch.value,
    ],
    // ``signal`` is forwarded into fetchPreview → axios → fetch. When
    // submittedTerm changes (i.e. the user clicks Search again with
    // updated input), TanStack Query aborts the prior signal and starts
    // a new fetch. The user always sees the latest submission's result.
    queryFn: ({ signal }) => fetchPreview(classifiedForFetch, signal),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
  return {
    classified: classifiedNow,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    data: query.data,
  };
}
